import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useId, type ReactNode } from 'react';
import { cn, disabledStyles, focusRing } from './utils';

export interface RadioOption<T extends string = string> {
  readonly value: T;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}

export interface RadioGroupProps<T extends string = string> {
  readonly legend: ReactNode;
  readonly value: T | undefined;
  readonly onValueChange: (value: T) => void;
  readonly options: readonly RadioOption<T>[];
  readonly name?: string;
  readonly disabled?: boolean;
  readonly orientation?: 'vertical' | 'horizontal';
  readonly className?: string;
  readonly hideLegend?: boolean;
  readonly error?: string;
}

/**
 * Grupo de rádio.
 *
 * A legenda é obrigatória por contrato de tipo: um grupo de rádio sem nome de
 * grupo faz o leitor de tela anunciar "opção 1 de 3" sem dizer 1 de quê. O
 * Radix já entrega a navegação por setas com rolagem circular (padrão APG).
 */
export function RadioGroup<T extends string = string>({
  legend,
  value,
  onValueChange,
  options,
  name,
  disabled = false,
  orientation = 'vertical',
  className,
  hideLegend = false,
  error,
}: RadioGroupProps<T>) {
  const baseId = useId();
  const errorId = `${baseId}-error`;
  const legendId = `${baseId}-legend`;

  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend
        id={legendId}
        className={cn('mb-2 text-sm font-medium text-content', hideLegend && 'qc-sr-only')}
      >
        {legend}
      </legend>
      <RadioGroupPrimitive.Root
        value={value}
        onValueChange={(next) => onValueChange(next as T)}
        name={name}
        disabled={disabled}
        orientation={orientation}
        /*
         * O `<legend>` rotula o `<fieldset>`, e o `role="radiogroup"` do Radix
         * é um `div` DENTRO dele — a associação não atravessa. Sem este
         * `aria-labelledby`, o leitor de tela anuncia "grupo" e o usuário
         * escolhe entre opções sem saber do quê: em "Efeito da regra"
         * (permitir × negar) e na matriz GUT, isso é escolher às cegas.
         */
        aria-labelledby={legendId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(
          'flex gap-3',
          orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap items-center',
          className,
        )}
      >
        {options.map((option) => {
          const itemId = `${baseId}-${option.value}`;
          const descriptionId = `${itemId}-description`;
          return (
            <div key={option.value} className="flex items-start gap-2.5">
              <RadioGroupPrimitive.Item
                id={itemId}
                value={option.value}
                disabled={option.disabled}
                aria-describedby={option.description ? descriptionId : undefined}
                className={cn(
                  'mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border',
                  'border-border-control bg-surface transition-colors',
                  'data-[state=checked]:border-brand',
                  focusRing,
                  disabledStyles,
                )}
              >
                <RadioGroupPrimitive.Indicator className="block h-2.5 w-2.5 rounded-full bg-brand" />
              </RadioGroupPrimitive.Item>
              <div className="min-w-0 flex-1">
                <label
                  htmlFor={itemId}
                  className={cn(
                    'cursor-pointer text-sm text-content',
                    option.disabled && 'cursor-not-allowed opacity-55',
                  )}
                >
                  {option.label}
                </label>
                {option.description ? (
                  <p id={descriptionId} className="mt-0.5 text-xs text-content-subtle">
                    {option.description}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </RadioGroupPrimitive.Root>
      {error ? (
        <p id={errorId} role="alert" className="mt-1.5 text-xs font-medium text-danger-content">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}
